import { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MaterialNode } from '@models/classes';
import {
    eTextureBlendMode,
    eTextureChannel,
    eTextureFit,
    eTextureHorizontalAlign,
    eTextureVerticalAlign,
} from '@models/classes/enums';
import { FileTextureLayer } from '@models/classes/materials/file-texture-layer';
import { TextureLayer } from '@models/classes/materials/texture-layer';
import { KbColor } from '@models/classes/primitives/kb-color';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { Renderer } from '../renderer';
import { TextureParameters } from '../texture-stack';

interface ICanvasSize {
    width: number;
    height: number;
}

export interface IDrawOperations {
    mask?: { context: CanvasRenderingContext2D };
    normalRotate?: { angle: number };
    flipNormalsRg?: boolean;
    colorize?: { color: KbColor };
}

const blendModeMap = {
    [eTextureBlendMode.normal]: 'source-over',
    [eTextureBlendMode.multiply]: 'multiply',
    [eTextureBlendMode.screen]: 'screen',
    [eTextureBlendMode.overlay]: 'overlay',
    [eTextureBlendMode.darken]: 'darken',
    [eTextureBlendMode.lighten]: 'lighten',
    [eTextureBlendMode.hue]: 'hue',
    [eTextureBlendMode.saturation]: 'saturation',
    [eTextureBlendMode.color]: 'color',
    [eTextureBlendMode.luminosity]: 'luminosity',
    [eTextureBlendMode.difference]: 'difference',
    [eTextureBlendMode.mask]: 'destination-in',
    [eTextureBlendMode.vector]: 'kb3d-vector', // special blending mode
};

const workingCanvases: HTMLCanvasElement[] = [];
/** Make sure to dispose the canvas after use! Otherwise you'll get a memory leak! */
function getWorkingCanvas(width: number, height: number) {
    let canvas: HTMLCanvasElement | undefined;
    for (let i = 0; i < workingCanvases.length; i++) {
        if (workingCanvases[i].width === 1) {
            canvas = workingCanvases[i];
            break;
        }
    }
    if (!canvas) {
        canvas = document.createElement('canvas');
        workingCanvases.push(canvas);
    }
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', {
        willReadFrequently: true,
    })!;
    context.clearRect(0, 0, canvas.width, canvas.height);
    return canvas;
}
function disposeWorkingCanvas(canvas: HTMLCanvasElement) {
    canvas.width = 1;
    canvas.height = 1;
}

export abstract class TextureLayerRenderer<T extends TextureLayer> extends Renderer<T, BaseTexture> {
    modelToken = Token.TextureLayer;

    paintTexture(
        texture: T,
        context: CanvasRenderingContext2D,
        parameters: TextureParameters,
        operations: IDrawOperations
    ) {}

    getNativeSize(texture: T): ICanvasSize {
        return { width: 1, height: 1 };
    }

    update3dInternal(node: ITrackedClass<T>) {
        this.viewer.updateTextureWarnings();
    }

    textureHasContent(node: ITrackedClass<T>): boolean {
        return false;
    }

    protected calculateOffsetAndDimensions(
        texture: T,
        ctxWidth: number,
        ctxHeight: number,
        sourceWidth: number,
        sourceHeight: number
    ) {
        let offsetX = texture.uOffset * ctxWidth,
            offsetY = texture.vOffset * ctxHeight,
            drawHeight = sourceHeight,
            drawWidth = sourceWidth;

        switch (texture.verticalFit) {
            case eTextureFit.contain:
                if (sourceHeight < ctxHeight) {
                    break;
                }
            // falls through
            case eTextureFit.stretch:
                drawHeight = ctxHeight;
        }
        switch (texture.horizontalFit) {
            case eTextureFit.contain:
                if (sourceWidth < ctxWidth) {
                    break;
                }
            // falls through
            case eTextureFit.stretch:
                drawWidth = ctxWidth;
        }
        if (texture.maintainAspect) {
            const minRatio = Math.min(drawWidth / sourceWidth, drawHeight / sourceHeight);
            drawWidth = minRatio * sourceWidth;
            drawHeight = minRatio * sourceHeight;
        }

        switch (texture.verticalAlign) {
            // left is offset with no adjustment
            case eTextureVerticalAlign.bottom:
                offsetY += ctxHeight - drawHeight;
                break;
            case eTextureVerticalAlign.middle:
                offsetY += (ctxHeight - drawHeight) / 2;
                break;
        }
        switch (texture.horizontalAlign) {
            // top is offset with no adjustment
            case eTextureHorizontalAlign.right:
                offsetX += ctxWidth - drawWidth;
                break;
            case eTextureHorizontalAlign.center:
                offsetX += (ctxWidth - drawWidth) / 2;
                break;
        }

        return {
            offsetX,
            offsetY,
            drawHeight,
            drawWidth,
        };
    }

    protected drawInBlendedContext(
        texture: T,
        context: CanvasRenderingContext2D,
        parameters: TextureParameters,
        cb: (context: CanvasRenderingContext2D) => void,
        operations: IDrawOperations
    ) {
        if (operations.mask) {
            const maskingCtx = operations.mask.context;
            delete operations.mask;
            // Transfer transform from context to masking context to mask what is drawn
            const transform = context.getTransform();
            maskingCtx.setTransform(transform);
            context.setTransform(1, 0, 0, 1, 0, 0);

            maskingCtx.globalCompositeOperation = 'source-in';
            cb(maskingCtx);
            maskingCtx.setTransform(1, 0, 0, 1, 0, 0);

            this.drawInBlendedContext(
                texture,
                context,
                parameters,
                ctx => {
                    ctx.drawImage(maskingCtx.canvas, 0, 0, ctx.canvas.width, ctx.canvas.height);
                },
                operations
            );
        } else if (Object.keys(operations).length > 0) {
            const tempCanvas = getWorkingCanvas(context.canvas.width, context.canvas.height);
            const tempContext = tempCanvas.getContext('2d')!;
            cb(tempContext);
            this.filterImageInPlace(tempCanvas, operations);

            this.drawInBlendedContext(
                texture,
                context,
                parameters,
                ctx => {
                    ctx.drawImage(tempCanvas, 0, 0, ctx.canvas.width, ctx.canvas.height);
                    disposeWorkingCanvas(tempCanvas);
                },
                operations
            );
        } else {
            context.globalAlpha = texture.strength / parameters.strength;
            switch (texture.blendMode) {
                case eTextureBlendMode.vector: {
                    const tempCanvas = getWorkingCanvas(context.canvas.width, context.canvas.height);
                    const tempContext = tempCanvas.getContext('2d')!;
                    cb(tempContext);
                    const srcData = tempContext.getImageData(0, 0, context.canvas.width, context.canvas.height).data;
                    const dstImageData = context.getImageData(0, 0, context.canvas.width, context.canvas.height);
                    const dstData = dstImageData.data;
                    const v1 = Vector3.Zero();
                    const v2 = Vector3.Zero();

                    const alphaScale = context.globalAlpha / 255;
                    for (let i = 0, n = srcData.length; i < n; i += 4) {
                        v1.copyFromFloats(dstData[i] / 128 - 1, dstData[i + 1] / 128 - 1, dstData[i + 2] / 256 - 0.5);
                        v2.copyFromFloats(srcData[i] / 128 - 1, srcData[i + 1] / 128 - 1, srcData[i + 2] / 256 - 0.5);
                        v2.scaleInPlace(srcData[i + 3] * alphaScale);
                        v1.addInPlace(v2);
                        v1.normalize();
                        dstData[i] = (v1.x + 1) * 128;
                        dstData[i + 1] = (v1.y + 1) * 128;
                        dstData[i + 2] = (v1.z + 1) * 128;
                        dstData[i + 3] = 255;
                    }
                    tempContext.putImageData(dstImageData, 0, 0);
                    context.drawImage(tempCanvas, 0, 0, context.canvas.width, context.canvas.height);
                    disposeWorkingCanvas(tempCanvas);

                    break;
                }

                default: {
                    (context.globalCompositeOperation as unknown) = blendModeMap[texture.blendMode] || 'source-over';
                    cb(context);
                    context.globalCompositeOperation = 'source-over';
                }
            }
            context.globalAlpha = 1;
        }
    }

    protected filterImageInPlace(buffer: HTMLCanvasElement, operations: IDrawOperations) {
        const ctx = buffer.getContext('2d')!;
        const width = buffer.width;
        const height = buffer.height;

        if (Object.keys(operations).length === 0) {
            return buffer;
        }

        if (operations.colorize) {
            const tempCanvas = getWorkingCanvas(width, height);
            const tmpCtx = tempCanvas.getContext('2d')!;

            const color = operations.colorize.color;
            delete operations.colorize;

            tmpCtx.globalCompositeOperation = 'source-over';
            tmpCtx.fillStyle = `rgb(${color.r * 255}, ${color.g * 255}, ${color.b * 255})`;
            tmpCtx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            tmpCtx.globalCompositeOperation = 'luminosity';
            tmpCtx.drawImage(buffer, 0, 0);
            tmpCtx.globalCompositeOperation = 'destination-in';
            tmpCtx.drawImage(buffer, 0, 0);
            ctx.drawImage(tempCanvas, 0, 0);
            disposeWorkingCanvas(tempCanvas);
        }

        if (Object.keys(operations).length === 0) {
            return buffer;
        }

        const imgData = ctx.getImageData(0, 0, width, height);
        const data = imgData.data;

        if (operations.normalRotate) {
            const rotateAngle = -1 * operations.normalRotate.angle;
            delete operations.normalRotate;
            const cosA = Math.cos((rotateAngle * Math.PI) / 180);
            const sinA = Math.sin((rotateAngle * Math.PI) / 180);

            for (let i = 0, n = data.length; i < n; i += 4) {
                const x1 = data[i] / 128 - 1;
                const y1 = data[i + 1] / 128 - 1;
                const x2 = cosA * x1 - sinA * y1;
                const y2 = sinA * x1 + cosA * y1;
                data[i] = (x2 + 1) * 128;
                data[i + 1] = (y2 + 1) * 128;
            }
        }
        if (operations.flipNormalsRg) {
            delete operations.flipNormalsRg;

            for (let i = 0, n = data.length; i < n; i += 4) {
                const r = data[i];
                data[i] = data[i + 1];
                data[i + 1] = r;
            }
        }
        ctx.putImageData(imgData, 0, 0);

        return buffer;
    }

    protected drawSourceToCanvas(
        texture: T,
        source: ImageBitmap | HTMLCanvasElement, // | CanvasImageSource,
        context: CanvasRenderingContext2D,
        parameters: TextureParameters,
        operations: IDrawOperations
    ) {
        let width = source.width as number,
            height = source.height as number;
        const ctxWidth = context.canvas.width,
            ctxHeight = context.canvas.height;
        const ctxPerfScale = {width: context.canvas.width / parameters.nativeSize.width, height: context.canvas.height / parameters.nativeSize.height}; 

        if (texture.colorize) {
            operations.colorize = { color: texture.colorize };
        }

        const firstTextureLayer =
            texture._parent?.textures
                .filter(t => t.channel === texture.channel && t.enabled)
                .findIndex(t => t === texture) === 0;

        if (texture.wAngle && texture.channel === eTextureChannel.normal && !firstTextureLayer) {
            operations.normalRotate = { angle: texture.wAngle };
        }
        if (
            texture instanceof FileTextureLayer &&
            texture.flipNormalsRg &&
            texture.channel === eTextureChannel.normal
        ) {
            operations.flipNormalsRg = true;
        }

        if (!firstTextureLayer) {
            const originalTransform = context.getTransform();

            const drawAt = this.calculateOffsetAndDimensions(texture, ctxWidth, ctxHeight, width, height);

            const pivotX = texture.uPivot * ctxWidth,
                pivotY = texture.vPivot * ctxHeight;

            if (texture.tile) {
                let patternCanvas: HTMLCanvasElement | undefined;
                let patternAttempt: CanvasPattern | undefined;
                // In the case of stretch modes, the draw image size will differ from the native size so we have to redraw it first
                // iOS is unable to draw ImageBitmaps to patterns, so we have to try/catch for iOS
                if (drawAt.drawWidth === width && drawAt.drawHeight === height) {
                    try {
                        patternAttempt = context.createPattern(source, 'repeat')!;
                    } catch (e) {
                        // Do nothing, fallback below
                    }
                }
                if (!patternAttempt) {
                    patternCanvas = getWorkingCanvas(drawAt.drawWidth, drawAt.drawHeight);
                    const patternContext = patternCanvas.getContext('2d')!;
                    patternContext.drawImage(source, 0, 0, drawAt.drawWidth, drawAt.drawHeight);

                    width = drawAt.drawWidth;
                    height = drawAt.drawHeight;
                    patternAttempt = context.createPattern(patternCanvas, 'repeat')!;
                }
                const pattern = patternAttempt;

                // Initial canvas transformations for angle and scaling
                context.translate(pivotX, pivotY);

                //if legacy mode enabled
                if (
                    texture._parent &&
                    texture._parent instanceof MaterialNode &&
                    texture._parent.legacyTextureStacking === false
                ) {
                    context.scale(texture.uScale * ctxPerfScale.width, texture.vScale * ctxPerfScale.height);
                    context.rotate((texture.wAngle * Math.PI) / 180);
                } else {
                    context.rotate((texture.wAngle * Math.PI) / 180);
                    context.scale(texture.uScale * ctxPerfScale.width, texture.vScale * ctxPerfScale.height);
                    if ((texture.uScale !== 1 || texture.vScale !== 1) && texture.wAngle !== 0) {
                        console.warn(
                            `Texture scale and angle are both altered on the texture layer "${texture.name}" with legacy mode enabled on the material "${texture._parent?.name}". The order of operations for scale and angle are now reversed on new materials. Please disable legacy mode on this material and adjust the texture stack properties as needed to preserve the desired behavior when legacy mode is deprecated.`
                        );
                    }
                }
                // Remove extra offset since the image will be tiled anyways
                drawAt.offsetX = drawAt.offsetX % width;
                drawAt.offsetY = drawAt.offsetY % height;

                // Initially set the draw size to cover the canvas
                let drawWidth = (ctxWidth - drawAt.offsetX) / texture.uScale;
                let drawHeight = (ctxHeight - drawAt.offsetY) / texture.vScale;

                // If the offset is greater than zero there will be a gap on the top or the left so we have to move the context over one tile
                if (drawAt.offsetX > 0) {
                    context.translate(-width, 0);
                    drawWidth += width;
                }
                if (drawAt.offsetY > 0) {
                    context.translate(0, -height);
                    drawHeight += height;
                }

                // add the offset and remove the pivot from earlier that was used to get the correct rotation and scaling matrix parts
                context.translate(drawAt.offsetX - pivotX, drawAt.offsetY - pivotY);

                // If the texture was scaled with a pivot, we need to draw the image larger to make sure there are no gaps
                if ((texture.uPivot !== 0 || texture.vPivot !== 0) && (texture.uScale !== 1 || texture.vScale !== 1)) {
                    const scaleOffsetU = Math.abs(Math.ceil(texture.uPivot / texture.uScale)) * width;
                    const scaleOffsetV = Math.abs(Math.ceil(texture.vPivot / texture.vScale)) * height;
                    if (scaleOffsetU !== 0 || scaleOffsetV !== 0) {
                        context.translate(-scaleOffsetU, -scaleOffsetV);
                        drawWidth += scaleOffsetU * 2;
                        drawHeight += scaleOffsetV * 2;
                    }
                }

                // If the texture was rotated, we need to draw the image larger to make sure there are no gaps
                if (texture.wAngle !== 0) {
                    const rotateOffsetU = Math.ceil(texture.uPivot / texture.uScale) * width;
                    const rotateOffsetV = Math.ceil(texture.vPivot / texture.vScale) * height;
                    if (rotateOffsetU > 0 || rotateOffsetV > 0) {
                        context.translate(-rotateOffsetU, -rotateOffsetV);
                        drawWidth += rotateOffsetU * 2;
                        drawHeight += rotateOffsetV * 2;
                    }
                }

                this.drawInBlendedContext(
                    texture,
                    context,
                    parameters,
                    innerContext => {
                        innerContext.fillStyle = pattern;
                        // Note that any rectangle start point outside of the box will not be rendered so all image transforms must be done on the canvas context
                        innerContext.fillRect(0, 0, drawWidth, drawHeight);
                        if (patternCanvas) {
                            disposeWorkingCanvas(patternCanvas);
                        }
                    },
                    operations
                );
            } else {
                context.translate(pivotX, pivotY);
                context.scale(texture.uScale * ctxPerfScale.width, texture.vScale * ctxPerfScale.height);
                context.rotate((texture.wAngle * Math.PI) / 180);
                context.translate(-pivotX, -pivotY);

                this.drawInBlendedContext(
                    texture,
                    context,
                    parameters,
                    innerContext => {
                        innerContext.drawImage(
                            source,
                            drawAt.offsetX,
                            drawAt.offsetY,
                            drawAt.drawWidth,
                            drawAt.drawHeight
                        );
                    },
                    operations
                );

                if (texture._visualize) {
                    context.strokeStyle = '#00FF00';
                    context.strokeRect(texture.uOffset * ctxWidth, texture.vOffset * ctxHeight, ctxWidth, ctxHeight);
                }
            }

            // reset the context
            context.setTransform(originalTransform);
        } else {
            // this block will never need to be masked because the layers using a clipping mask will never be the first layer
            if (Object.keys(operations).length > 0) {
                if (source instanceof HTMLCanvasElement) {
                    source = this.filterImageInPlace(source, operations);
                } else {
                    const tempCanvas = getWorkingCanvas(width, height);
                    const tempCtx = tempCanvas.getContext('2d')!;
                    tempCtx.drawImage(source, 0, 0);
                    source = this.filterImageInPlace(tempCanvas, operations);
                }
            }
            context.drawImage(source, 0, 0, ctxWidth, ctxHeight);
        }

        return context;
    }
}
